import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import type { OperationSchedule, ProposedScheduleLeg, ScheduleConflict } from '@aeronexo/contracts';
import {
  cancellationReasons,
  displayInstant,
  fromTimeInput,
  scheduleErrors,
  scheduleStates,
  toTimeInput,
} from './schedule-presentation.js';

export function PlanningHistory({
  schedule,
  timeZone,
}: {
  schedule: OperationSchedule;
  timeZone: string;
}) {
  return (
    <div className="planningHistory">
      <h3>Histórico do planejamento</h3>
      {!schedule.history.length && !schedule.cancellations.length ? (
        <p>O planejamento original ainda não foi alterado.</p>
      ) : null}
      {schedule.history.map((item) => (
        <details key={item.id}>
          <summary>
            Revisão {item.revision} · {displayInstant(item.at, timeZone, true)} · {item.actorName}
          </summary>
          <p>{item.reason}</p>
          <small>
            {item.actorRole === 'organization_administrator'
              ? 'Administrador da Organização'
              : 'Coordenador'}{' '}
            · {item.at}
          </small>
          {item.overrideJustification ? (
            <p>Justificativa do conflito: {item.overrideJustification}</p>
          ) : null}
          <ul>
            {item.after.legs?.map((leg) => {
              const before = item.before.legs?.find((l) => l.legId === leg.legId);
              const route = schedule.legs.find((l) => l.id === leg.legId);
              const oldDeparture = before?.revisedDeparture ?? before?.plannedDeparture;
              const oldArrival = before?.revisedArrival ?? before?.plannedArrival;
              const departure = leg.revisedDeparture ?? leg.plannedDeparture;
              const arrival = leg.revisedArrival ?? leg.plannedArrival;
              return (
                <li key={leg.legId}>
                  <strong>
                    {route ? `${route.originName} → ${route.destinationName}` : 'Trecho'}
                  </strong>
                  <br />
                  Antes:{' '}
                  {oldDeparture && oldArrival
                    ? `${displayInstant(oldDeparture, timeZone, true)} → ${displayInstant(oldArrival, timeZone, true)}`
                    : 'Não informado'}
                  <br />
                  Depois:{' '}
                  {departure && arrival
                    ? `${displayInstant(departure, timeZone, true)} → ${displayInstant(arrival, timeZone, true)}`
                    : 'Não informado'}
                </li>
              );
            })}
          </ul>
        </details>
      ))}
      {schedule.cancellations.map((item) => (
        <article key={item.id} className="planningCancellation">
          <strong>
            {item.target === 'operation'
              ? 'Operação cancelada'
              : item.target === 'leg'
                ? 'Trecho cancelado'
                : 'Trechos seguintes cancelados'}
          </strong>
          <p>
            {cancellationReasons[item.reasonCode]} · {item.reason}
          </p>
          <p>Impacto: {item.impact}</p>
          <small>
            {item.actorName} · {displayInstant(item.at, timeZone, true)}
          </small>
        </article>
      ))}
    </div>
  );
}

export function ConflictList({
  conflicts,
  timeZone,
  onOpenOperation,
}: {
  conflicts: readonly ScheduleConflict[];
  timeZone: string;
  onOpenOperation?: ((id: string) => void) | undefined;
}) {
  return (
    <ul className="planningConflicts">
      {conflicts.map((c, i) => (
        <li key={`${c.type}:${c.legId}:${c.from}:${i}`}>
          <strong>
            {c.hard
              ? 'Aeronave ocupada — alteração bloqueada'
              : 'Responsável com horários sobrepostos'}
          </strong>
          <span>
            {c.referenceCode} · {displayInstant(c.from, timeZone, true)} até{' '}
            {displayInstant(c.to, timeZone, true)}
          </span>
          {onOpenOperation ? (
            <button type="button" onClick={() => onOpenOperation(c.operationId)}>
              Abrir {c.referenceCode}
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function OperationPlanning({
  organizationId,
  operationId,
  active,
  onExpiredSession,
  onChanged,
}: {
  organizationId: string;
  operationId: string;
  active: boolean;
  onExpiredSession: () => Promise<void>;
  onChanged: () => Promise<void>;
}) {
  const [schedule, setSchedule] = useState<OperationSchedule>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reload, setReload] = useState(0);
  const [mode, setMode] = useState<'reschedule' | 'cancellation' | null>(null);
  const [utc, setUtc] = useState(false);
  const [legs, setLegs] = useState<ProposedScheduleLeg[]>([]);
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState('');
  const [impact, setImpact] = useState('');
  const [reasonCode, setReasonCode] = useState('customer_request');
  const [target, setTarget] = useState('operation');
  const [legId, setLegId] = useState('');
  const [password, setPassword] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const [conflicts, setConflicts] = useState<ScheduleConflict[]>([]);
  const [busy, setBusy] = useState(false);
  const attempt = useRef<{ canonical: string; id: string; key: string } | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const endpoint = `/v1/organizations/${organizationId}/operations/${operationId}/schedule`;
  const timeZone = utc ? 'UTC' : Intl.DateTimeFormat().resolvedOptions().timeZone;
  const fetchSchedule = useCallback(
    async (signal?: AbortSignal) => {
      const response = await fetch(endpoint, {
        credentials: 'include',
        ...(signal ? { signal } : {}),
      });
      if (response.status === 401) {
        await onExpiredSession();
        return undefined;
      }
      if (!response.ok) {
        const body = (await response.json()) as { code: string };
        throw new Error(scheduleErrors[body.code] ?? 'Não foi possível carregar o planejamento.');
      }
      return (await response.json()) as OperationSchedule;
    },
    [endpoint, onExpiredSession],
  );
  useEffect(() => {
    if (!active) {
      setPassword('');
      return;
    }
    // Keep the revision that was actually reviewed while a form is open.
    // Returning from another tab must not silently adopt a newer revision.
    if (mode) return;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    void fetchSchedule(controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setSchedule(value);
      })
      .catch((e: unknown) => {
        if (!controller.signal.aborted)
          setError(e instanceof Error ? e.message : 'Não foi possível carregar o planejamento.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [active, mode, reload, fetchSchedule]);
  const begin = (next: 'reschedule' | 'cancellation') => {
    if (!schedule) return;
    setMode(next);
    setError('');
    setSuccess('');
    setReason('');
    setImpact('');
    setOverride('');
    setPassword('');
    setReviewed(false);
    setConflicts([]);
    attempt.current = null;
    setTarget('operation');
    setLegId('');
    setLegs(
      schedule.legs
        .filter((l) => !l.cancelledAt)
        .map((l) => ({
          legId: l.id,
          revisedDeparture: l.revisedDeparture ?? l.plannedDeparture,
          revisedArrival: l.revisedArrival ?? l.plannedArrival,
        })),
    );
  };
  const post = async (suffix: string, body: unknown) => {
    const response = await fetch(`${endpoint}/${suffix}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const value = (await response.json()) as {
      code?: string;
      conflicts?: ScheduleConflict[];
      replayed?: boolean;
    };
    if (response.status === 401) {
      await onExpiredSession();
      throw new Error('Sua sessão expirou. Entre novamente.');
    }
    if (!response.ok)
      throw new Error(
        scheduleErrors[value.code ?? ''] ??
          'Não foi possível concluir. Seus dados foram preservados; tente novamente.',
      );
    return value;
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!schedule || !mode || busy) return;
    setBusy(true);
    setError('');
    try {
      if (!reviewed) {
        if (mode === 'reschedule') {
          const preview = await post('conflicts', { expectedRevision: schedule.revision, legs });
          setConflicts(preview.conflicts ?? []);
        }
        setReviewed(true);
      } else {
        const payload =
          mode === 'reschedule'
            ? {
                expectedRevision: schedule.revision,
                reason,
                legs,
                overrideJustification: override.trim() || null,
              }
            : {
                expectedRevision: schedule.revision,
                reasonCode,
                reason,
                impact,
                target,
                legId: target === 'operation' ? null : legId,
              };
        const canonical = JSON.stringify({ mode, payload });
        if (attempt.current?.canonical !== canonical)
          attempt.current = { canonical, id: crypto.randomUUID(), key: crypto.randomUUID() };
        await post(mode, {
          ...payload,
          ...(mode === 'reschedule'
            ? { revisionId: attempt.current.id }
            : { cancellationId: attempt.current.id }),
          idempotencyKey: attempt.current.key,
          password,
        });
        setMode(null);
        setPassword('');
        attempt.current = null;
        setReviewed(false);
        setSuccess(
          mode === 'reschedule'
            ? 'Horários reprogramados. O planejamento original foi preservado.'
            : 'Cancelamento registrado com motivo, impacto e histórico.',
        );
        setSchedule(await fetchSchedule());
        await onChanged();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível concluir a alteração.');
    } finally {
      setBusy(false);
      requestAnimationFrame(() => resultRef.current?.focus());
    }
  };
  const refresh = () => {
    setMode(null);
    setReviewed(false);
    setPassword('');
    attempt.current = null;
    setReload((r) => r + 1);
  };
  const activeLegs = schedule?.legs.filter((l) => !l.cancelledAt) ?? [];
  const canReschedule =
    schedule?.permissions.reschedule &&
    !['draft', 'closed', 'cancelled'].includes(schedule.state) &&
    activeLegs.length > 0 &&
    !activeLegs.some((l) => l.actualDeparture || l.actualArrival);
  const canCancel =
    schedule?.permissions.cancel &&
    !['draft', 'closed', 'cancelled'].includes(schedule.state) &&
    schedule.executionStatus !== 'completed';
  const hardConflict = conflicts.some((c) => c.hard);
  const responsibleConflict = conflicts.some((c) => !c.hard);
  return (
    <section
      id="operation-planning"
      className="operationPlanning"
      hidden={!active}
      aria-labelledby="planning-title"
      aria-busy={busy || loading}
    >
      <header className="planningHeading">
        <div>
          <h3 id="planning-title">Planejamento e horários</h3>
          <p>Consulte o original, revise horários e acompanhe cada alteração.</p>
        </div>
        <button type="button" disabled={busy || loading} onClick={refresh}>
          Atualizar planejamento
        </button>
      </header>
      <div ref={resultRef} tabIndex={-1} className="planningResult">
        {error ? (
          <p role="alert" className="workflowError">
            {error}
          </p>
        ) : null}
        {success ? (
          <p role="status" className="workflowSuccess">
            {success}
          </p>
        ) : null}
        {loading ? <p role="status">Carregando planejamento…</p> : null}
        {reviewed && mode ? (
          <p role="status">Revise as mudanças abaixo antes de confirmar.</p>
        ) : null}
      </div>
      {schedule && !loading ? (
        <>
          <div className="planningMeta">
            <span>
              {scheduleStates[schedule.state]} · revisão {schedule.revision}
            </span>
            <label>
              <span>Horários exibidos em</span>
              <select
                disabled={busy || Boolean(mode)}
                value={utc ? 'utc' : 'local'}
                onChange={(e) => setUtc(e.target.value === 'utc')}
              >
                <option value="local">{Intl.DateTimeFormat().resolvedOptions().timeZone}</option>
                <option value="utc">UTC</option>
              </select>
            </label>
          </div>
          <p className="planningHint">
            Fuso do registro: {schedule.displayTimeZone}. Planejado, revisado e realizado permanecem
            separados.
          </p>
          <div
            className="planningTable"
            tabIndex={0}
            role="region"
            aria-label="Horários por trecho"
          >
            <table>
              <thead>
                <tr>
                  <th>Trecho</th>
                  <th>Planejado</th>
                  <th>Revisado</th>
                  <th>Realizado</th>
                </tr>
              </thead>
              <tbody>
                {schedule.legs.map((l) => (
                  <tr key={l.id} className={l.cancelledAt ? 'agendaCancelled' : ''}>
                    <th scope="row">
                      {l.sequence}. {l.originName} → {l.destinationName}
                      {l.cancelledAt ? <small>Cancelado</small> : null}
                    </th>
                    <td>
                      {displayInstant(l.plannedDeparture, timeZone, true)}
                      <br />
                      {displayInstant(l.plannedArrival, timeZone, true)}
                    </td>
                    <td>
                      {l.revisedDeparture
                        ? displayInstant(l.revisedDeparture, timeZone, true)
                        : '—'}
                      <br />
                      {l.revisedArrival ? displayInstant(l.revisedArrival, timeZone, true) : '—'}
                    </td>
                    <td>
                      {l.actualDeparture ? displayInstant(l.actualDeparture, timeZone, true) : '—'}
                      <br />
                      {l.actualArrival ? displayInstant(l.actualArrival, timeZone, true) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!mode && schedule.conflicts.length ? (
            <>
              <h4>Conflitos do planejamento atual</h4>
              <ConflictList conflicts={schedule.conflicts} timeZone={timeZone} />
            </>
          ) : null}
          {!mode ? (
            <div className="workflowActions">
              {canReschedule ? (
                <button type="button" className="primaryButton" onClick={() => begin('reschedule')}>
                  Reprogramar horários
                </button>
              ) : null}
              {canCancel ? (
                <button
                  type="button"
                  className="dangerButton"
                  onClick={() => begin('cancellation')}
                >
                  Cancelar operação ou trechos
                </button>
              ) : null}
            </div>
          ) : null}
          {!schedule.permissions.reschedule && !schedule.permissions.cancel ? (
            <p className="readOnlyNote">
              Seu perfil permite consultar o planejamento e o histórico, sem alterar.
            </p>
          ) : null}
          {schedule.state === 'draft' ? (
            <p className="planningHint">
              Este é o planejamento inicial de um rascunho. A reprogramação fica disponível após o
              início da coordenação.
            </p>
          ) : null}
          {activeLegs.some((l) => l.actualDeparture || l.actualArrival) ? (
            <p className="planningHint">
              Há horários realizados. A reprogramação desta operação está bloqueada para preservar
              os registros de execução.
            </p>
          ) : null}
          {mode ? (
            <form className="planningForm" onSubmit={(event) => void submit(event)}>
              <h3>{mode === 'reschedule' ? 'Reprogramar horários' : 'Registrar cancelamento'}</h3>
              <fieldset disabled={busy || reviewed}>
                {mode === 'reschedule' ? (
                  <>
                    {legs.map((leg, i) => {
                      const source = schedule.legs.find((l) => l.id === leg.legId)!;
                      return (
                        <fieldset className="planningLeg" key={leg.legId}>
                          <legend>
                            {source.sequence}. {source.originName} → {source.destinationName}
                          </legend>
                          <div className="timeFields">
                            {(['revisedDeparture', 'revisedArrival'] as const).map((field) => (
                              <label key={field}>
                                <span>
                                  {field === 'revisedDeparture' ? 'Nova partida' : 'Nova chegada'} ·{' '}
                                  {timeZone}
                                </span>
                                <input
                                  type="datetime-local"
                                  step="1"
                                  required
                                  value={toTimeInput(leg[field], utc)}
                                  onChange={(e) => {
                                    try {
                                      const instant = fromTimeInput(e.target.value, utc);
                                      setLegs((current) =>
                                        current.map((l, index) =>
                                          index === i ? { ...l, [field]: instant } : l,
                                        ),
                                      );
                                      setError('');
                                    } catch (err) {
                                      setError(
                                        err instanceof Error ? err.message : 'Horário inválido.',
                                      );
                                    }
                                  }}
                                />
                              </label>
                            ))}
                          </div>
                        </fieldset>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <div className="timeFields">
                      <label>
                        <span>O que cancelar</span>
                        <select
                          value={target}
                          onChange={(e) => {
                            setTarget(e.target.value);
                            setLegId('');
                          }}
                        >
                          <option value="operation">Operação inteira</option>
                          <option value="leg">Um trecho</option>
                          <option value="remaining_legs">A partir de um trecho</option>
                        </select>
                      </label>
                      {target !== 'operation' ? (
                        <label>
                          <span>Trecho</span>
                          <select required value={legId} onChange={(e) => setLegId(e.target.value)}>
                            <option value="">Selecione</option>
                            {activeLegs
                              .filter((l) => !l.actualDeparture && !l.actualArrival)
                              .map((l) => (
                                <option key={l.id} value={l.id}>
                                  {l.sequence}. {l.originName} → {l.destinationName}
                                </option>
                              ))}
                          </select>
                        </label>
                      ) : null}
                      <label>
                        <span>Categoria do motivo</span>
                        <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
                          {Object.entries(cancellationReasons).map(([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <label>
                      <span>Impacto e providências</span>
                      <textarea
                        required
                        minLength={10}
                        maxLength={4000}
                        value={impact}
                        onChange={(e) => setImpact(e.target.value)}
                        placeholder="Descreva os efeitos sobre passageiros, equipe, aeronave e próximos trechos."
                      />
                    </label>
                  </>
                )}
                <label>
                  <span>Motivo da alteração</span>
                  <textarea
                    required
                    minLength={10}
                    maxLength={2000}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Explique por que o planejamento precisa mudar."
                  />
                </label>
              </fieldset>
              {reviewed ? (
                <div className="planningReview">
                  <h4>Confira antes de confirmar</h4>
                  {mode === 'reschedule' ? (
                    <>
                      <ul>
                        {legs.map((l) => {
                          const old = schedule.legs.find((s) => s.id === l.legId)!;
                          return (
                            <li key={l.legId}>
                              <strong>
                                {old.originName} → {old.destinationName}
                              </strong>
                              <br />
                              Atual:{' '}
                              {displayInstant(
                                old.revisedDeparture ?? old.plannedDeparture,
                                timeZone,
                                true,
                              )}{' '}
                              →{' '}
                              {displayInstant(
                                old.revisedArrival ?? old.plannedArrival,
                                timeZone,
                                true,
                              )}
                              <br />
                              Novo: {displayInstant(l.revisedDeparture, timeZone, true)} →{' '}
                              {displayInstant(l.revisedArrival, timeZone, true)}
                            </li>
                          );
                        })}
                      </ul>
                      <ConflictList conflicts={conflicts} timeZone={timeZone} />
                      {!conflicts.length ? (
                        <p>
                          Nenhuma sobreposição encontrada nesta consulta. Os horários serão
                          conferidos novamente ao salvar.
                        </p>
                      ) : null}
                      {responsibleConflict && schedule.permissions.approveConflict ? (
                        <label>
                          <span>Justificativa para manter a sobreposição do responsável</span>
                          <textarea
                            required
                            minLength={10}
                            maxLength={2000}
                            disabled={busy}
                            value={override}
                            onChange={(e) => setOverride(e.target.value)}
                          />
                        </label>
                      ) : null}
                      {responsibleConflict && !schedule.permissions.approveConflict ? (
                        <p role="alert">
                          Seu perfil não permite confirmar este conflito. Escolha outros horários.
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <p>
                        {target === 'operation'
                          ? 'A operação será cancelada.'
                          : target === 'leg'
                            ? 'O trecho selecionado será cancelado.'
                            : 'O trecho selecionado e os seguintes ainda ativos serão cancelados.'}{' '}
                        Os registros e horários realizados serão preservados.
                      </p>
                      <p>Motivo: {reason}</p>
                      <p>Impacto: {impact}</p>
                    </>
                  )}
                  {!hardConflict &&
                  (!responsibleConflict || schedule.permissions.approveConflict) ? (
                    <label>
                      <span>Confirme com sua senha</span>
                      <input
                        type="password"
                        autoComplete="current-password"
                        required
                        maxLength={128}
                        disabled={busy}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <small>A confirmação identifica o responsável por esta alteração.</small>
                    </label>
                  ) : null}
                </div>
              ) : null}
              <footer className="workflowActions">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setMode(null);
                    setPassword('');
                  }}
                >
                  Voltar sem alterar
                </button>
                {reviewed ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setReviewed(false);
                      setPassword('');
                      setConflicts([]);
                    }}
                  >
                    Revisar dados
                  </button>
                ) : null}
                <button
                  type="submit"
                  className={mode === 'cancellation' && reviewed ? 'dangerButton' : 'primaryButton'}
                  disabled={
                    busy ||
                    (reviewed &&
                      (hardConflict ||
                        (responsibleConflict && !schedule.permissions.approveConflict)))
                  }
                >
                  {busy
                    ? 'Aguarde…'
                    : !reviewed
                      ? 'Revisar alteração'
                      : mode === 'reschedule'
                        ? 'Confirmar reprogramação'
                        : 'Confirmar cancelamento'}
                </button>
              </footer>
            </form>
          ) : null}
          <PlanningHistory schedule={schedule} timeZone={timeZone} />
        </>
      ) : null}
    </section>
  );
}
